<template>
    <div class="basic-panel">
        <skeleton-loader v-if="showSkeleton" type="stats-panel" />

        <template v-else>
            <div class="basic-kpis">
                <kpi-card :label="$t('business.active_clients')" :value="activeCount" :delta="activeDelta"
                    :hint="$t('business.active_clients_hint')" />
                <kpi-card :label="$t('business.new_clients')" :value="joinedCount"
                    :hint="$t('business.new_clients_hint')" />
                <kpi-card :label="$t('business.lost_clients')" :value="leftCount"
                    :hint="$t('business.lost_clients_hint')" />
                <kpi-card :label="$t('business.avg_ticket')" :value="ticketLabel" :delta="ticketDelta"
                    :hint="$t('business.avg_ticket_hint')" />

                <div class="basic-summary">
                    <div class="summary-row">
                        <span class="summary-label">{{ $t('business.incomes') }}</span>
                        <span class="summary-value summary-value--in">{{ formatMoney(totalIncomes) }}</span>
                    </div>
                    <div class="summary-track">
                        <div class="summary-fill summary-fill--in" :style="{ width: `${incomeWidth}%` }" />
                    </div>

                    <div class="summary-row">
                        <span class="summary-label">{{ $t('business.expenses') }}</span>
                        <span class="summary-value summary-value--out">{{ formatMoney(totalExpenses) }}</span>
                    </div>
                    <div class="summary-track">
                        <div class="summary-fill summary-fill--out" :style="{ width: `${expenseWidth}%` }" />
                    </div>

                    <div class="summary-divider" />

                    <span class="summary-caption">{{ resultCaption }}</span>
                    <span class="summary-result">{{ formatMoney(result) }}</span>
                </div>
            </div>

            <div class="basic-charts">
                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.evolution') }}</span>
                        <v-menu offset-y nudge-bottom="6" transition="slide-y-transition">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="metric-button" v-on="on" v-bind="attrs">
                                    {{ metricLabel(metric) }}
                                    <v-icon size="14">mdi-chevron-down</v-icon>
                                </button>
                            </template>
                            <v-list dense color="surface" class="py-1">
                                <v-list-item v-for="option in metrics" :key="option" @click="metric = option">
                                    <v-list-item-title class="metric-option"
                                        :class="{ active: option === metric }">
                                        {{ metricLabel(option) }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                    <div v-if="!hasSeries" class="basic-empty">{{ $t('business.no_month_data') }}</div>
                    <div v-else class="basic-chart">
                        <line-chart :labels="monthLabels" :datasets="datasets" :value-formatter="metricFormatter"
                            :padding-top="12" :height="200" />
                    </div>
                </stat-card>

                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.plans') }}</span>
                        <group-by-selector :items="planModes" :value="planMode" @change="planMode = $event" />
                    </template>

                    <donut-breakdown v-if="planMode === 'clients'" :items="planClientItems" :size="120"
                        :empty-text="$t('business.no_plan_data')" />
                    <horizontal-bars v-else :items="planBillingItems" show-percent
                        :empty-text="$t('business.no_plan_data')" />
                </stat-card>
            </div>
        </template>
    </div>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KpiCard from "@/components/common/KpiCard";
import StatCard from "@/components/statistics/StatCard";
import DonutBreakdown from "@/components/common/DonutBreakdown";
import HorizontalBars from "@/components/common/HorizontalBars";
import LineChart from "@/components/common/LineChart";
import GroupBySelector from "@/components/statistics/GroupBySelector";
import {
    ACTIVITY_PALETTE, average, formatMoney, isActiveAt, leftMonth, loadCashYear, loadRoster, mergeRoster,
    monthOf, periodReference, previousPeriod, sumExpenses, sumIncomes,
} from "@/utils/Business";

const METRICS = ['active', 'joined', 'left', 'ticket', 'incomes', 'expenses', 'result']

const MONEY_METRICS = ['ticket', 'incomes', 'expenses', 'result']

const TREND_COLOR = '#66CCCC'

const NO_PLAN_COLOR = '#556070'

export default {
    name: "StatsBasic",
    components: {
        SkeletonLoader, KpiCard, StatCard, DonutBreakdown, HorizontalBars, LineChart, GroupBySelector,
    },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        period: {
            type: String,
            default: '',
        },
        cash: {
            type: Object,
            default: null,
        },
        payments: {
            type: Array,
            default: () => [],
        },
        clientsIncome: {
            type: Number,
            default: 0,
        },
        clientsIncomeByPeriod: {
            type: Object,
            default: () => ({}),
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loadingRoster: true,
            loadedOnce: false,
            clients: [],
            students: [],
            planTypes: [],
            months: {},
            metric: 'active',
            planMode: 'clients',
            metrics: METRICS,
        }
    },
    computed: {
        showSkeleton(vm = this) {
            return (vm.loading || vm.loadingRoster) && !vm.loadedOnce
        },
        planModes(vm = this) {
            void vm.$i18n.locale
            return [
                { name: vm.$t('business.by_clients'), value: 'clients' },
                { name: vm.$t('business.by_billing'), value: 'billing' },
            ]
        },
        roster(vm = this) {
            return mergeRoster(vm.clients, vm.students)
        },
        year(vm = this) {
            return Number(String(vm.period).slice(0, 4)) || vm.$moment().year()
        },
        reference(vm = this) {
            return periodReference(vm.period)
        },
        activeCount(vm = this) {
            return vm.roster.filter(client => isActiveAt(client, vm.reference)).length
        },
        activeDelta(vm = this) {
            const previous = periodReference(previousPeriod(vm.period))
            const difference = vm.activeCount - vm.roster.filter(client => isActiveAt(client, previous)).length
            if (!difference) return null
            return difference > 0 ? `+${difference}` : `${difference}`
        },
        joinedCount(vm = this) {
            return vm.roster.filter(client => monthOf(client.joinedAt) === vm.period).length
        },
        leftCount(vm = this) {
            return vm.roster.filter(client => leftMonth(client) === vm.period).length
        },
        periodPayments(vm = this) {
            return vm.payments.filter(payment => payment.period === vm.period)
        },
        ticket(vm = this) {
            return average(vm.periodPayments.map(payment => Number(payment.amount) || 0))
        },
        ticketLabel(vm = this) {
            return vm.ticket === null ? null : formatMoney(vm.ticket)
        },
        ticketDelta(vm = this) {
            const before = previousPeriod(vm.period)
            const previous = average(vm.payments
                .filter(payment => payment.period === before)
                .map(payment => Number(payment.amount) || 0))
            if (vm.ticket === null || previous === null) return null
            const difference = Math.round(vm.ticket - previous)
            if (!difference) return null
            return `${difference > 0 ? '+' : ''}${formatMoney(difference)}`
        },
        totalIncomes(vm = this) {
            return sumIncomes(vm.cash, vm.clientsIncome)
        },
        totalExpenses(vm = this) {
            return sumExpenses(vm.cash)
        },
        result(vm = this) {
            return vm.totalIncomes - vm.totalExpenses
        },
        summaryMax(vm = this) {
            return Math.max(vm.totalIncomes, vm.totalExpenses)
        },
        incomeWidth(vm = this) {
            return vm.summaryMax ? Math.round(vm.totalIncomes / vm.summaryMax * 100) : 0
        },
        expenseWidth(vm = this) {
            return vm.summaryMax ? Math.round(vm.totalExpenses / vm.summaryMax * 100) : 0
        },
        resultCaption(vm = this) {
            if (!vm.totalIncomes) return vm.$t('business.result')
            const margin = `${Math.round(vm.result / vm.totalIncomes * 100)}%`
            return vm.$t('business.result_with_margin', { margin })
        },
        monthLabels(vm = this) {
            void vm.$i18n.locale
            return Array.from({ length: 12 }, (item, i) => {
                return vm.$moment().month(i).format('MMM').replace('.', '').toUpperCase()
            })
        },
        series(vm = this) {
            const current = vm.$moment().format('YYYY-MM')
            return Array.from({ length: 12 }, (item, i) => {
                const key = `${vm.year}-${String(i + 1).padStart(2, '0')}`
                return key > current ? null : vm.metricValue(key)
            })
        },
        hasSeries(vm = this) {
            return vm.series.some(value => value !== null)
        },
        datasets(vm = this) {
            const theme = vm.$vuetify.theme.currentTheme
            return [{
                label: vm.metricLabel(vm.metric),
                data: vm.series,
                borderColor: TREND_COLOR,
                borderWidth: 2.5,
                backgroundColor: `${TREND_COLOR}22`,
                pointBackgroundColor: theme.primary,
                pointBorderColor: theme.surface,
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: true,
                lineTension: 0.35,
            }]
        },
        metricFormatter(vm = this) {
            return MONEY_METRICS.includes(vm.metric) ? formatMoney : null
        },
        clientsById(vm = this) {
            const map = {}
            vm.clients.forEach(client => {
                map[client.id] = client
            })
            return map
        },
        planGroups(vm = this) {
            return [
                ...vm.planTypes.map((plan, i) => ({
                    id: plan.id,
                    label: plan.name,
                    color: plan.color || ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length],
                })),
                { id: null, label: vm.$t('business.no_plan'), color: NO_PLAN_COLOR },
            ]
        },
        planClientItems(vm = this) {
            const counts = {}
            vm.roster
                .filter(client => isActiveAt(client, vm.reference))
                .forEach(client => {
                    const key = client.planId || 'none'
                    counts[key] = (counts[key] || 0) + 1
                })

            return vm.planGroups
                .map(group => ({
                    label: group.label,
                    color: group.color,
                    value: counts[group.id || 'none'] || 0,
                    text: String(counts[group.id || 'none'] || 0),
                }))
                .filter(item => item.value > 0)
        },
        planBillingItems(vm = this) {
            const totals = {}
            vm.periodPayments.forEach(payment => {
                const planId = payment.planId || vm.clientsById[payment.clientId]?.planId || 'none'
                totals[planId] = (totals[planId] || 0) + (Number(payment.amount) || 0)
            })

            return vm.planGroups
                .map(group => ({
                    label: group.label,
                    color: group.color,
                    value: totals[group.id || 'none'] || 0,
                    text: formatMoney(totals[group.id || 'none'] || 0),
                }))
                .filter(item => item.value > 0)
                .sort((a, b) => b.value - a.value)
        },
    },
    mounted() {
        this.load()
        this.loadYear()
    },
    methods: {
        formatMoney,
        metricLabel(metric) {
            return this.$t(`business.metric_${metric}`)
        },
        metricValue(key) {
            const vm = this
            const reference = periodReference(key)
            const monthCash = key === vm.period ? vm.cash : vm.months[key]
            const autoIncome = vm.clientsIncomeByPeriod[key] || 0

            if (vm.metric === 'active') return vm.roster.filter(client => isActiveAt(client, reference)).length
            if (vm.metric === 'joined') return vm.roster.filter(client => monthOf(client.joinedAt) === key).length
            if (vm.metric === 'left') return vm.roster.filter(client => leftMonth(client) === key).length
            if (vm.metric === 'ticket') {
                return average(vm.payments
                    .filter(payment => payment.period === key)
                    .map(payment => Number(payment.amount) || 0))
            }

            const incomes = monthCash || autoIncome > 0 ? sumIncomes(monthCash, autoIncome) : null
            const expenses = monthCash ? sumExpenses(monthCash) : null
            if (vm.metric === 'incomes') return incomes
            if (vm.metric === 'expenses') return expenses
            if (incomes === null && expenses === null) return null
            return (incomes || 0) - (expenses || 0)
        },
        async load() {
            const vm = this
            if (!vm.companyId) return
            vm.loadingRoster = true
            try {
                const { clients, students, planTypes } = await loadRoster(vm.companyId)
                vm.clients = clients
                vm.students = students
                vm.planTypes = planTypes
            } catch (error) {
                console.error('[business] loadRoster', error?.code, error?.message)
            } finally {
                vm.loadingRoster = false
            }
        },
        async loadYear() {
            const vm = this
            if (!vm.companyId) return
            try {
                vm.months = await loadCashYear(vm.companyId, vm.year)
            } catch (error) {
                console.error('[business] loadCashYear', error?.code, error?.message)
                vm.months = {}
            }
        },
    },
    watch: {
        loading(value) {
            if (!value) this.loadedOnce = true
        },
        loadingRoster(value) {
            if (!value && !this.loading) this.loadedOnce = true
        },
        period(value, previous) {
            if (String(value).slice(0, 4) !== String(previous).slice(0, 4)) this.loadYear()
        },
    },
}
</script>

<style scoped>
.basic-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.basic-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr) 1.6fr;
    gap: 10px;
}

.basic-summary {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 12px 14px;
    border-radius: 12px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    min-width: 0;
}

.summary-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}

.summary-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.summary-value {
    font-size: var(--fs-body);
    font-weight: 700;
}

.summary-value--in {
    color: var(--v-success-base);
}

.summary-value--out {
    color: var(--v-error-base);
}

.summary-track {
    height: 4px;
    border-radius: 3px;
    background: var(--v-surface2-base);
    overflow: hidden;
}

.summary-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.summary-fill--in {
    background: var(--v-success-base);
}

.summary-fill--out {
    background: var(--v-error-base);
}

.summary-divider {
    height: 1px;
    margin: 3px 0;
    background: var(--v-outline-base);
}

.summary-caption {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.summary-result {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.1;
    color: var(--v-txtPrimary-base);
}

.basic-charts {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    gap: 16px;
}

.chart-title {
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.metric-button {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 26px;
    padding: 0 6px 0 10px;
    flex-shrink: 0;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.metric-button:hover {
    border-color: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.metric-option {
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.metric-option.active {
    color: var(--v-primary-base);
}

.theme--light .metric-option.active {
    color: var(--v-accent-base);
}

.basic-chart {
    height: 200px;
}

.basic-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 1264px) {
    .basic-kpis {
        grid-template-columns: repeat(4, 1fr);
    }

    .basic-summary {
        grid-column: span 4;
    }
}

@media (max-width: 960px) {
    .basic-kpis {
        grid-template-columns: repeat(2, 1fr);
    }

    .basic-summary {
        grid-column: span 2;
    }

    .basic-charts {
        grid-template-columns: 1fr;
    }
}
</style>
